import { PI, RAD2DEG, TWO_PI } from "../math/constants";
import type { OrbitalElements } from "../math/orbital_elements";
import { periapsis, apoapsis } from "../math/orbital_elements";
import { BodyRegistry } from "../bodies/body_registry";
import type { SimulationState } from "../simulation/simulation_state";
import { trueAnomalyAt } from "../simulation/orbit_propagator";

/**
 * Interface for individual conditions (victory or failure).
 */
export interface ICondition {
  readonly id: string;
  readonly isVictoryCondition: boolean;
  evaluate(state: SimulationState, bodies: BodyRegistry, jd: number): boolean;
}

export interface ConditionResult {
  conditionId: string;
  isMet: boolean;
  isVictory: boolean;
}

/**
 * Evaluates all conditions each tick and reports which are newly met.
 */
export class ConditionEvaluator {
  private readonly conditions: ICondition[] = [];
  private readonly metConditions = new Set<string>();

  addCondition(condition: ICondition): void {
    this.conditions.push(condition);
  }

  get allConditions(): readonly ICondition[] {
    return this.conditions;
  }

  /** Check if a specific condition has been met (ever). */
  isConditionMet(conditionId: string): boolean {
    return this.metConditions.has(conditionId);
  }

  /** Check if all victory conditions are met. */
  allVictoryConditionsMet(): boolean {
    for (const c of this.conditions) {
      if (c.isVictoryCondition && !this.metConditions.has(c.id)) {
        return false;
      }
    }
    return true;
  }

  /** Check if any failure condition is met. */
  anyFailureConditionMet(): boolean {
    for (const c of this.conditions) {
      if (!c.isVictoryCondition && this.metConditions.has(c.id)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Evaluate all conditions. Returns conditions that NEWLY became met this tick.
   */
  evaluate(
    state: SimulationState,
    bodies: BodyRegistry,
    jd: number,
  ): ConditionResult[] {
    const results: ConditionResult[] = [];

    for (const condition of this.conditions) {
      if (this.metConditions.has(condition.id)) continue; // Already met

      const met = condition.evaluate(state, bodies, jd);
      if (met) {
        this.metConditions.add(condition.id);
        results.push({
          conditionId: condition.id,
          isMet: true,
          isVictory: condition.isVictoryCondition,
        });
      }
    }

    return results;
  }

  /** Reset all condition states. */
  reset(): void {
    this.metConditions.clear();
  }
}

// --- Victory Conditions ---

/**
 * Victory: achieve orbit around a specified body with parameters in range.
 */
export class OrbitAroundBodyCondition implements ICondition {
  readonly id: string;
  readonly isVictoryCondition = true;

  targetBody: string;
  minPeriapsis: number;   // km
  maxPeriapsis: number;   // km
  minApoapsis: number;    // km
  maxApoapsis: number;    // km
  maxEccentricity: number;
  maxInclination: number; // degrees

  constructor(
    id: string,
    targetBody: string,
    opts: {
      minPeriapsis: number;
      maxPeriapsis: number;
      minApoapsis: number;
      maxApoapsis: number;
      maxEccentricity?: number;
      maxInclination?: number;
    },
  ) {
    this.id = id;
    this.targetBody = targetBody;
    this.minPeriapsis = opts.minPeriapsis;
    this.maxPeriapsis = opts.maxPeriapsis;
    this.minApoapsis = opts.minApoapsis;
    this.maxApoapsis = opts.maxApoapsis;
    this.maxEccentricity = opts.maxEccentricity ?? 1.0;
    this.maxInclination = opts.maxInclination ?? 180.0;
  }

  evaluate(state: SimulationState, _bodies: BodyRegistry, _jd: number): boolean {
    // Must be in the target body's SOI
    if (state.currentSOIBody !== this.targetBody) return false;

    const orbit = state.currentOrbit;

    // Check elliptical (e < 1)
    if (orbit.eccentricity >= 1.0) return false;

    const peri = periapsis(orbit);
    const apo = apoapsis(orbit);
    const incDeg = orbit.inclination * RAD2DEG;

    return (
      peri >= this.minPeriapsis &&
      peri <= this.maxPeriapsis &&
      apo >= this.minApoapsis &&
      apo <= this.maxApoapsis &&
      orbit.eccentricity <= this.maxEccentricity &&
      incDeg <= this.maxInclination
    );
  }
}

/**
 * Victory: observe a full orbit (complete 360 degrees of true anomaly).
 */
export class FullOrbitCondition implements ICondition {
  readonly id: string;
  readonly isVictoryCondition = true;
  readonly targetBody: string;

  private accumulatedAngle: number = 0;
  private lastTrueAnomaly: number = -1;

  constructor(id: string, targetBody: string) {
    this.id = id;
    this.targetBody = targetBody;
  }

  evaluate(state: SimulationState, bodies: BodyRegistry, jd: number): boolean {
    if (state.currentSOIBody !== this.targetBody) return false;
    if (state.currentOrbit.eccentricity >= 1.0) return false;

    const gm = bodies.getGM(this.targetBody);
    const nu = trueAnomalyAt(state.currentOrbit, gm, jd);

    if (this.lastTrueAnomaly >= 0) {
      let delta = nu - this.lastTrueAnomaly;
      // Handle wrap-around
      if (delta < -PI) delta += TWO_PI;
      if (delta > PI) delta -= TWO_PI;
      if (delta > 0) this.accumulatedAngle += delta;
    }

    this.lastTrueAnomaly = nu;
    return this.accumulatedAngle >= TWO_PI;
  }
}

/**
 * Victory: heliocentric orbit matches a target planet's orbit.
 * Checks that the player's solar orbit has semi-major axis within tolerance
 * of the target body's SMA, and eccentricity is reasonably low.
 * Used for interplanetary transfer missions where SOI transitions aren't needed.
 */
export class HeliocentricTransferCondition implements ICondition {
  readonly id: string;
  readonly isVictoryCondition = true;
  readonly targetBody: string;
  /** Fractional tolerance on SMA match (e.g., 0.05 = ±5%) */
  readonly smaTolerance: number;
  /** Max eccentricity for the heliocentric orbit */
  readonly maxEccentricity: number;

  constructor(id: string, targetBody: string, smaTolerance: number = 0.05, maxEccentricity: number = 0.15) {
    this.id = id;
    this.targetBody = targetBody;
    this.smaTolerance = smaTolerance;
    this.maxEccentricity = maxEccentricity;
  }

  evaluate(state: SimulationState, bodies: BodyRegistry, _jd: number): boolean {
    // Must be in heliocentric orbit
    if (state.currentSOIBody !== "Sun") return false;

    const targetBodyData = bodies.getByName(this.targetBody);
    if (!targetBodyData?.orbitalElements) return false;

    const targetSMA = targetBodyData.orbitalElements.semiMajorAxis;
    const orbit = state.currentOrbit;

    // Must be elliptical
    if (orbit.eccentricity >= 1.0) return false;

    // SMA within tolerance
    const smaRatio = orbit.semiMajorAxis / targetSMA;
    if (Math.abs(smaRatio - 1.0) > this.smaTolerance) return false;

    // Eccentricity reasonably low
    if (orbit.eccentricity > this.maxEccentricity) return false;

    return true;
  }
}

// --- Failure Conditions ---

/**
 * Failure: spacecraft crashes into a body (distance < radius).
 */
export class CrashCondition implements ICondition {
  readonly id: string;
  readonly isVictoryCondition = false;

  constructor(id: string) {
    this.id = id;
  }

  evaluate(state: SimulationState, bodies: BodyRegistry, _jd: number): boolean {
    const body = bodies.getByName(state.currentSOIBody);
    if (!body) return false;
    const distance = state.spacecraft.state.position.magnitude;
    return distance <= body.meanRadius;
  }
}

/**
 * Failure: all fuel exhausted.
 */
export class FuelExhaustedCondition implements ICondition {
  readonly id: string;
  readonly isVictoryCondition = false;

  constructor(id: string) {
    this.id = id;
  }

  evaluate(state: SimulationState, _bodies: BodyRegistry, _jd: number): boolean {
    return state.spacecraft.fuelMass <= 0;
  }
}

/**
 * Failure: exceeded maximum mission time.
 */
export class TimeLimitCondition implements ICondition {
  readonly id: string;
  readonly isVictoryCondition = false;
  readonly maxDurationDays: number;

  constructor(id: string, maxDurationDays: number) {
    this.id = id;
    this.maxDurationDays = maxDurationDays;
  }

  evaluate(state: SimulationState, _bodies: BodyRegistry, jd: number): boolean {
    return (jd - state.startTimeJD) > this.maxDurationDays;
  }
}
